// Single frontend source of truth for identity-document labels/point values
// — mirrors backend/src/identity-check-points.ts exactly. Used by both the
// tenant panel (IdentityVerificationPanel.tsx) and the client portal
// (PortalPage.tsx) so the reference table they each display, and the doc-type
// labels/options they each offer, can never drift apart from one another.
// Actual scoring (points/pointsSummary) always comes from the backend on
// every response — this file is for labels and the reference table only.

export type IdentityDocType =
  | 'passport'
  | 'birth-certificate'
  | 'citizenship-certificate'
  | 'drivers-licence'
  | 'proof-of-age-card'
  | 'tertiary-student-id'
  | 'government-employee-id'
  | 'medicare-card'
  | 'bank-statement'
  | 'utility-bill'
  | 'council-rates-notice'
  | 'property-lease-agreement'
  | 'other'

export const IDENTITY_DOC_LABELS: Record<IdentityDocType, string> = {
  passport: 'Passport',
  'birth-certificate': 'Birth Certificate',
  'citizenship-certificate': 'Citizenship Certificate',
  'drivers-licence': "Driver's Licence",
  'proof-of-age-card': 'Proof of Age Card',
  'tertiary-student-id': 'Tertiary Student ID',
  'government-employee-id': 'Government Employee ID',
  'medicare-card': 'Medicare Card',
  'bank-statement': 'Bank Statement',
  'utility-bill': 'Utility Bill',
  'council-rates-notice': 'Council Rates Notice',
  'property-lease-agreement': 'Property Lease Agreement',
  other: 'Other ID',
}

// Which guide-box shape DocumentScanner.tsx should show while scanning this
// doc type — 'card' for a standard ID-1 card (Medicare card, student/staff
// ID, proof-of-age card), 'page' for a full A4 page (certificates,
// statements, bills, notices, agreements). Cards and passport aren't offered
// through the manual upload flow (see PortalPage.tsx's MANUAL_DOC_TYPES) but
// are classified here too for completeness.
export const IDENTITY_DOC_SHAPE: Record<IdentityDocType, 'card' | 'page'> = {
  passport: 'page',
  'birth-certificate': 'page',
  'citizenship-certificate': 'page',
  'drivers-licence': 'card',
  'proof-of-age-card': 'card',
  'tertiary-student-id': 'card',
  'government-employee-id': 'card',
  'medicare-card': 'card',
  'bank-statement': 'page',
  'utility-bill': 'page',
  'council-rates-notice': 'page',
  'property-lease-agreement': 'page',
  other: 'page',
}

// Grouped for the reference table both views render — order matters (highest
// tier first). 'other' is deliberately excluded: it's a placeholder/legacy
// value, not a real document type someone would pick.
export const IDENTITY_DOC_TIERS: { title: string; points: number; note: string; types: IdentityDocType[] }[] = [
  {
    title: 'Primary Documents',
    points: 70,
    note: 'Usually restricted to one',
    types: ['passport', 'birth-certificate', 'citizenship-certificate'],
  },
  {
    title: 'Secondary Documents',
    points: 40,
    note: 'Photo IDs and government cards',
    types: ['drivers-licence', 'proof-of-age-card', 'tertiary-student-id', 'government-employee-id'],
  },
  {
    title: 'Supporting Documents',
    points: 25,
    note: 'Financial and utility records',
    types: ['medicare-card', 'bank-statement', 'utility-bill', 'council-rates-notice', 'property-lease-agreement'],
  },
]
